import React, { useEffect, useRef, useState } from 'react';
import './AskRiaInputBar.css';

// Reusable "Ask Ria" input bar — gradient pill with add/attach button, text
// input, mic (speech-to-text) button, and send button. Used on the Dashboard
// Ria chat card and on the Conversations page (Ria/Weekly/Monthly tabs and
// all personal/group chats) so the design stays consistent everywhere.
const AskRiaInputBar = ({
  value,
  onChange,
  onSend,
  placeholder = 'Ask Ria...',
  loading = false,
  disabled = false,
  showAddButton = true,
  onAddClick,
  addButtonTitle = 'Add',
  addButtonDisabled = false,
  className = '',
}) => {
  const [isRecording, setIsRecording] = useState(false);
  const recognitionRef = useRef(null);
  const valueRef = useRef(value);

  useEffect(() => {
    valueRef.current = value;
  }, [value]);

  // Stop any in-flight recognition session on unmount
  useEffect(() => () => {
    if (recognitionRef.current) recognitionRef.current.stop();
  }, []);

  const startRecording = () => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) {
      alert('Speech recognition is not supported in your browser. Please use Chrome or Edge.');
      return;
    }

    const recognition = new SpeechRecognition();
    recognition.lang = 'en-US';
    recognition.interimResults = false;
    recognition.continuous = false;

    recognition.onresult = (event) => {
      const transcript = event.results[0][0].transcript;
      onChange(valueRef.current ? valueRef.current + ' ' + transcript : transcript);
    };

    recognition.onerror = (event) => {
      console.error('Speech recognition error:', event.error);
      if (event.error === 'not-allowed') {
        alert('Microphone access was denied. Please allow microphone permissions.');
      }
      setIsRecording(false);
      recognitionRef.current = null;
    };

    recognition.onend = () => {
      setIsRecording(false);
      recognitionRef.current = null;
    };

    recognition.start();
    recognitionRef.current = recognition;
    setIsRecording(true);
  };

  const stopRecording = () => {
    if (recognitionRef.current) recognitionRef.current.stop();
  };

  const handleVoiceClick = () => {
    if (isRecording) {
      stopRecording();
    } else {
      startRecording();
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      onSend();
    }
  };

  return (
    <div className={`ask-ria-bar ${className}`}>
      {showAddButton && (
        <button
          type="button"
          className="ask-ria-bar-add-btn"
          onClick={onAddClick}
          disabled={addButtonDisabled}
          title={addButtonTitle}
        >
          <svg width="25" height="25" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
            <line x1="12" y1="5" x2="12" y2="19" />
            <line x1="5" y1="12" x2="19" y2="12" />
          </svg>
        </button>
      )}
      <input
        type="text"
        className="ask-ria-bar-input"
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={handleKeyDown}
      />
      <button
        type="button"
        className={`ask-ria-bar-voice-btn ${isRecording ? 'recording' : ''}`}
        onClick={handleVoiceClick}
        title={isRecording ? 'Stop Recording' : 'Start Recording'}
      >
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" />
          <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
          <line x1="12" y1="19" x2="12" y2="23" />
          <line x1="8" y1="23" x2="16" y2="23" />
        </svg>
      </button>
      <button
        type="button"
        className="ask-ria-bar-send-btn"
        onClick={onSend}
        disabled={disabled}
        title="Send"
      >
        {loading ? (
          <div className="ask-ria-bar-send-spinner" />
        ) : (
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#381f83" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <line x1="12" y1="19" x2="12" y2="5" />
            <polyline points="5 12 12 5 19 12" />
          </svg>
        )}
      </button>
    </div>
  );
};

export default AskRiaInputBar;
